import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { downloadCsv } from '../utils/api';
import { localDateStr } from '../utils/dates';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/FeedbackContext';
import Badge from '../components/Badge';
import StateBlock from '../components/StateBlock';
import EntityLink from '../components/EntityLink';

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [monthly, setMonthly] = useState(null);
  const [monthlyError, setMonthlyError] = useState(false);
  function getDefaultEndMonth() {
    const d = new Date();
    d.setDate(1); // avoid month-length rollover issues
    d.setMonth(d.getMonth() - 1); // default to last month - current month is always empty
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  }
  const DEFAULT_END_MONTH = getDefaultEndMonth();
  const [endMonth, setEndMonth] = useState(DEFAULT_END_MONTH);
  const [gapModal, setGapModal] = useState(null); // { title, rows, loading } | null
  const [fteGaps, setFteGaps] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [downloadingReport, setDownloadingReport] = useState(false);

  useEffect(() => {
    api.get('/dashboard-monthly/fte-needing-invoice').then(r => setFteGaps(r.data.rows)).catch(() => setFteGaps([]));
  }, []);

  function showGaps(monthStart, monthEnd, type, title) {
    const params = new URLSearchParams({ type, month_start: monthStart, month_end: monthEnd, title });
    window.open(`/dashboard-detail?${params.toString()}`, '_blank');
  }

  useEffect(() => {
    api.get('/dashboard').then(r => setData(r.data)).catch(() => setError(true));
  }, []);

  // Payment advice reminder (2026-10-22): verified payments with no advice
  // sent, and payments not verified yet. Admins / managers only.
  const [advice, setAdvice] = useState(null);
  useEffect(() => {
    if (!['admin', 'manager'].includes(user?.role)) return;
    api.get('/remittances/advice-summary').then(r => setAdvice(r.data)).catch(() => setAdvice(null));
  }, [user?.role]);

  useEffect(() => {
    setMonthly(null);
    setMonthlyError(false);
    const params = { end: endMonth, months: 6 };
    api.get('/dashboard-monthly', { params }).then(r => setMonthly(r.data.months)).catch(() => setMonthlyError(true));
  }, [endMonth]);

  function fmt(n) {
    return `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  }

  async function downloadHexawareReport() {
    setDownloadingReport(true);
    try {
      // Year to date: Jan of the ending month's year through the ending month (server sets the range)
      const [y, m] = endMonth.split('-').map(Number);
      const endName = new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'short' });
      const range = m === 1 ? `Jan ${y}` : `Jan–${endName} ${y}`;
      await downloadCsv('/hexaware-report/xlsx', `Hexaware Report YTD ${range}.xlsx`, { end: endMonth });
    } catch (e) {
      toast('Could not generate the Hexaware report', 'error');
    } finally {
      setDownloadingReport(false);
    }
  }

  function sumMonths(arr, field) {
    return (arr || []).reduce((s, m) => s + (m[field] || 0), 0);
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Overview of your accounts payable and receivable operations.</p>
        </div>
      </div>

      {error && <StateBlock type="error" />}
      {!error && !data && <StateBlock type="loading" />}

      {data && (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Financial Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            <SummaryCard label="AR Outstanding" tone="ar" value={fmt(data.financial_summary.ar_outstanding)}
              sub={data.financial_summary.ar_outstanding_consulting
                ? `Hexaware ${fmt(data.financial_summary.ar_outstanding - data.financial_summary.ar_outstanding_consulting)} · Consulting ${fmt(data.financial_summary.ar_outstanding_consulting)}`
                : 'Awaiting verified payment from clients'} onClick={() => navigate('/ar-invoices')} />
            <SummaryCard label="AP Outstanding" tone="ap" value={fmt(data.financial_summary.ap_outstanding)} sub="Owed to vendors" onClick={() => navigate('/ap-invoices')} />
            <SummaryCard label="Overdue AR" tone="ar" value={fmt(data.financial_summary.overdue_ar)}
              sub={data.financial_summary.overdue_ar_consulting
                ? `Hexaware ${fmt(data.financial_summary.overdue_ar - data.financial_summary.overdue_ar_consulting)} · Consulting ${fmt(data.financial_summary.overdue_ar_consulting)}`
                : 'Past due receivables'} danger onClick={() => navigate('/ar-invoices')} />
            <SummaryCard label="AP Ready to Pay (after EPD)" tone="ap" value={fmt(data.financial_summary.ap_ready_to_pay)}
              sub={`Before EPD: ${fmt(data.financial_summary.ap_ready_to_pay_before_epd)} · ${data.financial_summary.ap_ready_to_pay_count ?? 0} invoice(s) in the Payments Queue`} onClick={() => navigate('/ap-payments')} />
          </div>
        </>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Monthly Breakdown</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: 'var(--muted)' }}>Ending month:</label>
          <input
            type="month"
            value={endMonth}
            onChange={e => setEndMonth(e.target.value)}
            style={{ fontSize: 12, padding: '4px 8px' }}
          />
          {endMonth !== DEFAULT_END_MONTH && (
            <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setEndMonth(DEFAULT_END_MONTH)}>
              Reset to default
            </button>
          )}
          {user?.role === 'admin' && (
            <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={downloadHexawareReport} disabled={downloadingReport}
              title="Excel workbook for the 6 months shown: monthly AR, outstanding & aging, active consultants, AP & margin (internal)">
              {downloadingReport ? 'Preparing…' : '↓ Hexaware Report (Excel)'}
            </button>
          )}
        </div>
      </div>
      <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
        What's been raised, received, and paid each month — and where we're still catching up on data.
      </p>
      {monthlyError && <StateBlock type="error" />}
      {!monthlyError && !monthly && <StateBlock type="loading" />}
      {monthly && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 28 }}>
          {monthly.map(m => <MonthCard key={m.label} month={m} navigate={navigate} fmt={fmt} showGaps={showGaps} />)}
        </div>
      )}
      {fteGaps && fteGaps.length > 0 && (
        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>FTE Placements</h3>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
            One-time placement fees - not part of monthly billing, so these never show up above. Billable date = Date of Joining + 90 days (the guarantee period) - the date TMV becomes eligible to invoice Hexaware.
          </p>
          <table className="data-table">
            <thead><tr><th>Contractor</th><th>WO #</th><th>Type</th><th>Date of Joining</th><th>Billable Date</th><th>Invoice Status</th></tr></thead>
            <tbody>
              {fteGaps.map(r => {
                const today = localDateStr();
                const isEligible = r.billable_date && r.billable_date <= today;
                let statusLabel, statusColor;
                if (r.invoice_status === 'void') { statusLabel = 'Void'; statusColor = 'var(--muted)'; }
                else if (r.invoice_status === 'paid') { statusLabel = `Paid (${r.invoice_number})`; statusColor = 'var(--success-text)'; }
                else if (r.invoice_status === 'draft' && isEligible) { statusLabel = `Draft - ready to submit (${r.invoice_number})`; statusColor = 'var(--warning, #f59e0b)'; }
                else if (r.invoice_status === 'draft') { statusLabel = `Draft - not yet eligible (${r.invoice_number})`; statusColor = 'var(--muted)'; }
                else if (r.invoice_status === 'partial') { statusLabel = `Partially paid (${r.invoice_number})`; statusColor = 'var(--warning, #f59e0b)'; }
                else if (r.invoice_number) { statusLabel = `Sent (${r.invoice_number})`; statusColor = 'var(--info-text)'; }
                else if (isEligible) { statusLabel = 'Eligible - not yet raised'; statusColor = 'var(--danger-text)'; }
                else { statusLabel = 'Not yet eligible'; statusColor = 'var(--muted)'; }
                return (
                  <tr key={r.wo_id} style={{ cursor: 'pointer' }} onClick={() => navigate('/work-orders')}>
                    <td><EntityLink type="contractor" id={r.contractor_id}>{r.contractor}</EntityLink></td>
                    <td><EntityLink type="work_order" id={r.wo_id}>{r.client_wo_number}</EntityLink></td>
                    <td>{r.engagement_type === 'fte_placement' ? 'FTE Placement' : 'Recurring Referral'}</td>
                    <td>{r.start_date || '-'}</td>
                    <td>{r.billable_date || '-'}</td>
                    <td style={{ color: statusColor, fontWeight: 600 }}>
                      <EntityLink type="ar_invoice" id={r.invoice_id} style={{ color: statusColor }}>{statusLabel}</EntityLink>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}


      {data && (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Overview</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
            <SummaryCard label="Active Work Orders" value={data.headline.active_work_orders} sub="Currently active engagements" onClick={() => navigate('/work-orders')} />
            <SummaryCard label="Active Consultants" value={data.headline.active_consultants} sub="Contractors on active WOs" onClick={() => navigate('/contractors')} />
            <SummaryCard label="Partial AR" tone="ar" value={fmt(data.financial_summary.partial_ar)} sub="Invoices partly settled" onClick={() => navigate('/ar-invoices')} />
            <SummaryCard label="Partial AP" tone="ap" value={fmt(data.financial_summary.partial_ap)} sub="Invoices partly settled" onClick={() => navigate('/ap-invoices')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            <SummaryCard label="AR Raised (6mo)" tone="ar" value={fmt(sumMonths(data.monthly_trend.ar, 'raised'))} sub="Total invoiced, last 6 months" onClick={() => navigate('/ar-invoices')} />
            <SummaryCard label="AR Paid (6mo)" tone="ar" value={fmt(sumMonths(data.monthly_trend.ar, 'paid'))} sub="Verified payments, last 6 months" onClick={() => navigate('/ar-invoices')} />
            <SummaryCard label="AP Raised (6mo)" tone="ap" value={fmt(sumMonths(data.monthly_trend.ap, 'raised'))} sub="Total invoiced, last 6 months" onClick={() => navigate('/ap-invoices')} />
            <SummaryCard label="AP Paid (6mo)" tone="ap" value={fmt(sumMonths(data.monthly_trend.ap, 'paid'))} sub="Verified payments, last 6 months" onClick={() => navigate('/ap-invoices')} />
          </div>

          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Action Required</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
            <SummaryCard label="Pending Approvals" value={data.action_required.pending_approvals} sub="AP invoices waiting on Hexaware payment" onClick={() => navigate('/ap-invoices?pay=waiting_ar')} />
            <SummaryCard label="Missing Sell Rate" value={data.action_required.missing_sell_rate} sub="Active WOs missing a rate" onClick={() => navigate('/work-orders')} />
            <SummaryCard label="Incomplete Profiles" value={data.action_required.incomplete_profiles} sub="Contractors missing details" onClick={() => navigate('/contractors')} />
            <SummaryCard label="Unverified Payments" value={data.action_required.unverified_payments} sub="Recorded but not yet verified" onClick={() => navigate('/ar-invoices')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
            <SummaryCard label="Contractors w/o Invoices" value={data.action_required.contractors_no_invoices} sub="Active but never invoiced" onClick={() => navigate('/contractors')} />
            <SummaryCard label="Vendors w/o Invoices" value={data.action_required.vendors_no_invoices} sub="Active but never invoiced" onClick={() => navigate('/vendors')} />
            {advice && (
              <SummaryCard label="Payment advice not sent" tone="ap" danger={advice.unsent.payees > 0}
                value={`${advice.unsent.payees} payee${advice.unsent.payees === 1 ? '' : 's'}`}
                sub={advice.unsent.payments ? `${fmt(advice.unsent.total)}, oldest payment ${advice.unsent.oldest}` : `All verified payments since ${advice.since} sent`}
                onClick={() => navigate(`/vendor-remittances?unsent=1&from=${advice.unsent.oldest || advice.since}&to=${advice.today}`)} />
            )}
            {advice && (
              <SummaryCard label="Payments not verified yet" tone="ap" value={advice.unverified.payments}
                sub={advice.unverified.payments ? `${fmt(advice.unverified.total)} since ${advice.unverified.oldest} (advice blocked)` : 'Nothing blocking payment advice'}
                onClick={() => navigate(`/ap-payments?tab=made&from=${advice.unverified.oldest || advice.since}&to=${advice.today}&verified=unverified`)} />
            )}
          </div>

          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
            AP Received — AR Not Yet Raised {data.warnings.ap_without_ar.length > 0 && <Badge variant="danger">{data.warnings.ap_without_ar.length}</Badge>}
          </h3>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>
            Vendor/contractor was paid for this work, but no matching client invoice has been raised yet. Aim to keep this at zero.
          </p>
          {data.warnings.ap_without_ar.length === 0 ? (
            <StateBlock type="empty" title="All caught up" message="Every AP invoice has a matching AR invoice raised." />
          ) : (
            <table className="data-table" style={{ marginBottom: 28 }}>
              <thead>
                <tr><th>AP Invoice</th><th>Work Order</th><th>Contractor</th><th>Amount</th><th>Invoice Date</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {data.warnings.ap_without_ar.map(w => (
                  <tr key={w.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/ap-invoices/${w.id}`)}>
                    <td><EntityLink type="ap_invoice" id={w.id}>{w.invoice_number}</EntityLink></td>
                    <td><EntityLink type="work_order" id={w.wo_id}>{w.client_wo_number}</EntityLink></td>
                    <td><EntityLink type="contractor" id={w.contractor_id}>{w.contractor}</EntityLink></td>
                    <td>{fmt(w.amount_to_pay)}</td>
                    <td>{w.invoice_date}</td>
                    <td>{w.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Overdue AR Invoices</h3>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>Net 30 from invoice date.</p>
          {data.warnings.overdue_ar.length === 0 ? (
            <StateBlock type="empty" title="Nothing overdue" message="All AR invoices are within terms." />
          ) : (
            <table className="data-table" style={{ marginBottom: 28 }}>
              <thead>
                <tr><th>Invoice</th><th>Client</th><th>Outstanding</th><th>Due Date</th><th>Days Overdue</th></tr>
              </thead>
              <tbody>
                {data.warnings.overdue_ar.map(w => (
                  <tr key={w.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/ar-invoices/${w.id}`)}>
                    <td><EntityLink type="ar_invoice" id={w.id}>{w.invoice_number}</EntityLink></td>
                    <td><EntityLink type="client" id={w.client_id}>{w.client}</EntityLink></td>
                    <td>{fmt(w.outstanding)}</td>
                    <td>{w.due_date}</td>
                    <td><Badge variant="danger">{w.days_overdue}d</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>AP Invoices Pending Payment</h3>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>Payments Queue invoices 30+ days past invoice date (Net 30). Outstanding = amount due now after the Hexaware-payment cap.</p>
          {data.warnings.ap_pending_payment.length === 0 ? (
            <StateBlock type="empty" title="Nothing pending" message="No Payments Queue invoices are past Net 30." />
          ) : (
            <table className="data-table" style={{ marginBottom: 28 }}>
              <thead>
                <tr><th>Invoice</th><th>Payee</th><th>Outstanding</th><th>Invoice Date</th><th>Days Pending</th></tr>
              </thead>
              <tbody>
                {data.warnings.ap_pending_payment.map(w => (
                  <tr key={w.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/ap-invoices/${w.id}`)}>
                    <td><EntityLink type="ap_invoice" id={w.id}>{w.invoice_number}</EntityLink></td>
                    <td><EntityLink type={w.vendor_id ? 'vendor' : 'contractor'} id={w.vendor_id || w.contractor_id}>{w.payee}</EntityLink></td>
                    <td>{fmt(w.outstanding)}</td>
                    <td>{w.invoice_date}</td>
                    <td><Badge variant="warning">{w.days_pending}d</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Recent Activity</h3>
          {data.recent_activity.length === 0 ? (
            <StateBlock type="empty" title="No activity yet" message="Create a Work Order or invoice to get started." />
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Type</th><th>Description</th><th>Entity</th><th>Amount</th><th>Date</th><th>Status</th></tr>
              </thead>
              <tbody>
                {data.recent_activity.map((a, i) => (
                  <tr key={i}>
                    <td>{a.type}</td>
                    <td><EntityLink type={a.link_type} id={a.link_id}>{a.description}</EntityLink></td>
                    <td><EntityLink type={a.entity_type} id={a.entity_id}>{a.entity}</EntityLink></td>
                    <td>{a.amount != null ? fmt(a.amount) : '—'}</td>
                    <td>{(a.date || '').split(' ')[0]}</td>
                    <td><Badge>{a.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      {gapModal && <GapModal modal={gapModal} onClose={() => setGapModal(null)} navigate={navigate} />}
    </div>
  );
}

function GapModal({ modal, onClose, navigate }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
          padding: 24, width: 720, maxWidth: '95vw', maxHeight: '70vh', overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{modal.title}</h3>
          <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 13 }} onClick={onClose}>Close</button>
        </div>
        {modal.loading && <StateBlock type="loading" />}
        {modal.error && <StateBlock type="error" />}
        {!modal.loading && !modal.error && modal.rows.length === 0 && (
          <StateBlock type="empty" title="Nothing here" message="No gaps found for this month." />
        )}
        {!modal.loading && !modal.error && modal.rows.length > 0 && (() => {
          const sample = modal.rows[0];
          const fmt = n => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
          const isInvoiceType = 'invoice_number' in sample;
          const isActiveType = 'engagement_type' in sample;
          const target = modal.title.startsWith('AR') ? '/ar-invoices' : modal.title.startsWith('AP') ? '/ap-invoices' : '/work-orders';
          return (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Contractor</th><th>WO #</th>
                  {isInvoiceType && <><th>Invoice #</th><th>Amount</th><th>Paid</th><th>Outstanding</th></>}
                  {isActiveType && <><th>Type</th><th>Status</th></>}
                </tr>
              </thead>
              <tbody>
                {modal.rows.map((r, i) => (
                  <tr key={r.wo_id || r.invoice_number || i} style={{ cursor: 'pointer' }} onClick={() => navigate(target)}>
                    <td>{r.contractor}</td>
                    <td>{r.client_wo_number}</td>
                    {isInvoiceType && <>
                      <td>{r.invoice_number}</td>
                      <td>{fmt(r.amount)}</td>
                      <td>{fmt(r.paid)}</td>
                      <td>{fmt(r.outstanding)}</td>
                    </>}
                    {isActiveType && <>
                      <td>{r.engagement_type === 'contract_placement' ? 'Contract' : 'Referral'}</td>
                      <td>{r.status}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })()}
      </div>
    </div>
  );
}

function MonthCard({ month: m, navigate, fmt, showGaps }) {
  // Amber = still pending (money to receive/pay, or a gap in the month
  // still in progress). Red stays reserved for gaps in a closed month -
  // those aren't pending any more, they were missed.
  const pendingBox = { background: 'var(--pending-bg)', color: 'var(--pending-text)', borderRadius: 'var(--radius)', padding: '1px 8px' };
  const gapStyle = (value) => {
    if (value <= 0) return null;
    return m.is_current ? pendingBox : { color: 'var(--danger-text)' };
  };

  const row = (label, value, onClick, valueStyle) => (
    <div
      onClick={onClick}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', cursor: onClick ? 'pointer' : 'default' }}
    >
      <span style={{ fontSize: 14, color: 'var(--muted)' }}>{label}</span>
      <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', ...valueStyle }}>{value}</span>
    </div>
  );

  const moneyRow = (label, countAmt, onClick, pending) => {
    const highlight = pending && countAmt.count > 0;
    return (
      <div
        onClick={onClick}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', cursor: onClick ? 'pointer' : 'default' }}
      >
        <span style={{ fontSize: 14, color: 'var(--muted)' }}>{label}</span>
        <span style={{ textAlign: 'right', ...(highlight ? pendingBox : null) }}>
          <span style={{ fontSize: 17, fontWeight: 700 }}>{countAmt.count}</span>
          {' '}
          <span style={{ fontSize: 13, color: highlight ? 'inherit' : 'var(--muted)' }}>({fmt(countAmt.amount)})</span>
        </span>
      </div>
    );
  };

  const sectionHeader = (label, accent) => (
    <div style={{ fontSize: 19, fontWeight: 700, color: accent, textAlign: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)', marginBottom: 4 }}>
      {label}
    </div>
  );

  const panel = (bg) => ({ background: bg, borderRadius: 'var(--radius)', padding: '0 12px 4px' });

  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 20 }}>
      <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 14, letterSpacing: 0.5 }}>
        {m.label}{m.is_current && <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--muted)', marginLeft: 8 }}>(in progress)</span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 24 }}>
        <div>{row('Active Consultants', m.active_consultants, () => showGaps(m.month_start, m.month_end, 'active_consultants', `Active Consultants — ${m.label}`))}</div>
        <div>{row('Active Contracts', m.active_contracts, () => showGaps(m.month_start, m.month_end, 'active_contracts', `Active Contracts — ${m.label}`))}</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 16, marginTop: 8 }}>
        <div style={panel('var(--ar-bg)')}>
          {sectionHeader('AR', 'var(--ar-accent)')}
          {row('Invoices Raised', m.ar_raised, () => showGaps(m.month_start, m.month_end, 'ar_raised', `AR Invoices Raised — ${m.label}`))}
          {row('Drafted', m.ar_drafted, () => showGaps(m.month_start, m.month_end, 'ar_drafted', `AR Drafted — ${m.label}`))}
          {row('To Be Raised', m.ar_to_be_raised, () => showGaps(m.month_start, m.month_end, 'ar_to_be_raised', `AR To Be Raised — ${m.label}`), gapStyle(m.ar_to_be_raised))}
          {moneyRow('Payments Received', m.ar_payments_received, () => showGaps(m.month_start, m.month_end, 'ar_payments_received', `AR Payments Received — ${m.label}`))}
          {moneyRow('Payments To Receive', m.ar_payments_to_receive, () => showGaps(m.month_start, m.month_end, 'ar_payments_to_receive', `AR Payments To Receive — ${m.label}`), true)}
        </div>
        <div style={panel('var(--ap-bg)')}>
          {sectionHeader('AP', 'var(--ap-accent)')}
          {row('Invoices Received', m.ap_received, () => showGaps(m.month_start, m.month_end, 'ap_received', `AP Invoices Received — ${m.label}`))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 14, color: 'transparent' }}>&nbsp;</span>
            <span style={{ fontSize: 17, fontWeight: 700, color: 'transparent' }}>&nbsp;</span>
          </div>
          {row('Invoices Not Received', m.ap_not_received, () => showGaps(m.month_start, m.month_end, 'ap_not_received', `AP Not Received — ${m.label}`), gapStyle(m.ap_not_received))}
          {moneyRow('Payments Made', m.ap_payments_made, () => showGaps(m.month_start, m.month_end, 'ap_payments_made', `AP Payments Made — ${m.label}`))}
          {moneyRow('Payments To Make', m.ap_payments_to_make, () => showGaps(m.month_start, m.month_end, 'ap_payments_to_make', `AP Payments To Make — ${m.label}`), true)}
        </div>
      </div>
    </div>
  );
}

// tone: 'ar' (teal) / 'ap' (tan) tints the card to its side of the ledger.
function SummaryCard({ label, value, sub, danger, tone, onClick }) {
  const bg = tone === 'ar' ? 'var(--ar-bg)' : tone === 'ap' ? 'var(--ap-bg)' : 'var(--card)';
  const accent = tone === 'ar' ? 'var(--ar-accent)' : tone === 'ap' ? 'var(--ap-accent)' : 'var(--muted)';
  return (
    <div
      onClick={onClick}
      style={{
        background: bg, border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
        padding: 18, cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ fontSize: 12, color: accent, fontWeight: tone ? 600 : 400, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: danger ? 'var(--danger-text)' : 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{sub}</div>
    </div>
  );
}
